


import { useState } from "react";
import { loginUser, resetPassword } from "../authService";
import RegisterPage from "./RegisterPage";
import { useLanguage } from "../i18n/LanguageProvider";
import { useNavigate } from "react-router-dom";
import { markBiometricOfferAfterLogin } from "../services/biometricAuth";
import { FaChevronRight, FaEnvelope, FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import { Capacitor } from "@capacitor/core";
import AppleSignInButton from "../components/AppleSignInButton";

function LoginPage() {
  const isNativeAuthLayout = Capacitor.isNativePlatform();
  const { t, language } = useLanguage();
  const [showRegister, setShowRegister] = useState(() => {
    if (typeof window === "undefined") return false;
    const shouldReturnToRegister =
      window.sessionStorage?.getItem("vaultquest:returnToRegister") === "1";
    if (shouldReturnToRegister) {
      window.sessionStorage?.removeItem("vaultquest:returnToRegister");
    }
    return shouldReturnToRegister;
  });
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [view, setView] = useState("LOGIN"); // "LOGIN" | "RESET"
  const [showPassword, setShowPassword] = useState(false);
  const [focusedLoginField, setFocusedLoginField] = useState("");
  const vaultQuestInfoLabel = language === "en" ? "ⓘ What is VaultQuest?" : "ⓘ Was ist VaultQuest?";
  const registerPrompt = language === "en" ? "No account yet?" : "Noch kein Account?";
  const registerAction = language === "en" ? "Register now" : "Jetzt registrieren";

  const loginInputShellStyle = (fieldName) => ({
    height: 60,
    borderRadius: 14,
    background:
      "linear-gradient(180deg, rgba(18,16,54,0.96) 0%, rgba(11,9,42,0.98) 100%)",
    display: "flex",
    alignItems: "center",
    gap: 12,
    fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
    width: "100%",
    maxWidth: 340,
    marginLeft: "auto",
    marginRight: "auto",
    padding: "0 16px",
    boxSizing: "border-box",
    border:
      focusedLoginField === fieldName
        ? "1px solid rgba(255,42,166,0.72)"
        : "1px solid rgba(166,129,255,0.24)",
    boxShadow:
      focusedLoginField === fieldName
        ? "inset 0 1px 0 rgba(255,255,255,0.08), 0 0 0 1px rgba(122,43,255,0.18)"
        : "inset 0 1px 0 rgba(255,255,255,0.06), 0 12px 26px rgba(0,0,0,0.22)",
    transition: "border-color 160ms ease, box-shadow 160ms ease, background 160ms ease",
  });

  const loginInputIconStyle = {
    color: "#b975ff",
    filter: "drop-shadow(0 2px 5px rgba(122,43,255,0.22))",
    flexShrink: 0,
  };

  const loginInputStyle = {
    border: "none",
    outline: "none",
    width: "100%",
    minWidth: 0,
    fontSize: 16,
    fontWeight: 850,
    letterSpacing: 0.2,
    fontStyle: "normal",
    color: "rgba(255,255,255,0.90)",
    background: "transparent",
    fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
  };

const handleLogin = async (e) => {
  e.preventDefault();

  setError("");



  try {
    const loggedInUser = await loginUser(email, password);
    markBiometricOfferAfterLogin(loggedInUser?.uid);
 } catch (err) {
  const code = String(err?.code || "");

 if (
  code === "auth/invalid-login-credentials" ||
  code === "auth/wrong-password" ||
  code === "auth/user-not-found"
) {
  setError(t("login.errors.invalidCredentials"));
  return;
}

if (code === "auth/too-many-requests") {
  setError(t("login.errors.tooManyRequests"));
  return;
}

if (code === "auth/network-request-failed") {
  setError(t("login.errors.network"));
  return;
}

setError(t("login.errors.generic"));
}
};


const handleResetPassword = async () => {
  setError("");

  const mail = String(email || "").trim();

  if (!mail) {
  setError(t("reset.errors.enterEmailFirst"));
  return;
}

  try {
    await resetPassword(mail, language);

    // bewusst neutral, damit niemand prüfen kann, ob es den Account gibt
    setError(t("reset.messages.neutralSuccess"));
  } catch (err) {
    const code = String(err?.code || "");

   if (code.includes("auth/invalid-email")) {
  setError(t("reset.errors.invalidEmail"));
} else if (code.includes("auth/user-not-found")) {
  setError(t("reset.messages.neutralSuccess"));
} else if (code.includes("auth/too-many-requests")) {
  setError(t("reset.errors.tooManyRequests"));
} else {
  setError(t("reset.errors.generic"));
} 
  }
};

  // Umschalten auf Registrierung
  if (showRegister) {
    return <RegisterPage onBackToLogin={() => setShowRegister(false)} />;
  }

  return (
    <>
    {view === "LOGIN" ? (
<div
  className={`vq-auth-page${isNativeAuthLayout ? " is-native" : ""}`}
  style={{
    height: "100dvh",
    display: "flex",
    justifyContent: "center",
    alignItems: "stretch",
    background: "#000",
    padding: 0,
    boxSizing: "border-box",
    overflow: "hidden",
  }}
>
    <div
     className="vq-auth-surface"
     style={{
  width: "100%",
  maxWidth: "430px",
  height: "100dvh",
  borderRadius: 0,
  overflow: "hidden",
  background: "linear-gradient(90deg, #080427 0%, #020047 100%)",
  position: "relative",
  boxShadow: "none",
  padding: "calc(26px + var(--vq-safe-area-top)) calc(18px + var(--vq-safe-area-right)) calc(10px + var(--vq-safe-area-bottom)) calc(18px + var(--vq-safe-area-left))",
  boxSizing: "border-box",
  fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
}}
    >
      <style>{`
        .vault-login-input::placeholder {
          color: rgba(225, 218, 255, 0.52);
          opacity: 1;
        }

        .vault-login-input:-webkit-autofill {
          -webkit-text-fill-color: rgba(255, 255, 255, 0.92);
          caret-color: #ffffff;
          transition: background-color 9999s ease-in-out 0s;
        }
      `}</style>
      {/* Logo + Title */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: 5,
        }}
      >
        <img
  src="/vaultquest-logo.png"
  alt="VaultQuest"
  onClick={() => navigate("/landing")}
  style={{
    width: 200,
    height: 180,
    objectFit: "contain",
    marginBottom: 14,
    cursor: "pointer",
  }}
/>

       <div
  onClick={() => navigate("/landing")}
  style={{
    fontSize: 46,
    fontStyle: "normal",
    fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
    marginTop: -6,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: "uppercase",
    background: "linear-gradient(90deg, #ff007f 0%, #7a2bff 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    marginBottom: 30,
    cursor: "pointer",
  }}
>
  VAULTQUEST
</div> 
      </div>

      {/* Form */}
      <form onSubmit={handleLogin} style={{ display: "grid", gap: 14 }}>
        {/* Email */}
        <div
          className="vq-auth-control"
          style={loginInputShellStyle("email")}
        >
          <FaEnvelope aria-hidden="true" size={18} style={loginInputIconStyle} />
          <input
            className="vault-login-input"
            type="text"
            placeholder={t("login.emailOrUsernamePlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setFocusedLoginField("email")}
            onBlur={() => setFocusedLoginField("")}
            style={loginInputStyle}
          />
        </div>

        {/* Password */}
        <div
          className="vq-auth-control"
          style={loginInputShellStyle("password")}
        >
          <FaLock aria-hidden="true" size={18} style={loginInputIconStyle} />
          <input
            className="vault-login-input"
            type={showPassword ? "text" : "password"}
            placeholder={t("login.passwordPlaceholder")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={() => setFocusedLoginField("password")}
            onBlur={() => setFocusedLoginField("")}
            style={loginInputStyle}
          />
          <button
            type="button"
            onClick={() => setShowPassword((current) => !current)}
            aria-label={
              showPassword
                ? language === "en"
                  ? "Hide password"
                  : "Passwort verbergen"
                : language === "en"
                  ? "Show password"
                  : "Passwort anzeigen"
            }
            style={{
              border: "none",
              background: "transparent",
              color: "rgba(225,218,255,0.72)",
              width: 34,
              height: 34,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              padding: 0,
              cursor: "pointer",
            }}
          >
            {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
          </button>
        </div>

        {/* Account actions */}
     <div
  className="vq-auth-control"
  style={{
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: -4,
    width: "100%",
    maxWidth: 340,
    marginLeft: "auto",
    marginRight: "auto",
  }}
>
  <button
    type="button"
    onClick={() => {
      setError("");
      setView("RESET");
    }}
    style={{
      background: "transparent",
      border: "none",
      color: "rgba(255,255,255,0.70)",
      fontWeight: 850,
      letterSpacing: 0.35,
      textTransform: "none",
      textAlign: "center",
      cursor: "pointer",
      fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
      fontStyle: "normal",
      padding: "2px 0",
      boxSizing: "border-box",
      fontSize: 11,
      textShadow: "0 0 16px rgba(255,255,255,0.10)",
      whiteSpace: "nowrap",
    }}
  >
    {t("login.forgotPassword")}
  </button>
</div>

        {/* Login Button */}
        <button
          className="vq-auth-control"
          type="submit"
          style={{
            marginTop: 24,
            height: 66,
            borderRadius: 14,
            border: "1px solid rgba(255,255,255,0.32)",
            width: "100%",
            maxWidth: 360,
            marginLeft: "auto",
            marginRight: "auto",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 54px",
            boxSizing: "border-box",
            background: "linear-gradient(135deg, #ff0a8a 0%, #c018f4 48%, #7a2bff 100%)",
            color: "white",
            fontWeight: 1000,
            fontSize: 22,
            fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
            fontStyle: "italic",
            letterSpacing: 1.2,
            textTransform: "uppercase",
            cursor: "pointer",
            boxShadow: "0 18px 42px rgba(0,0,0,0.36), 0 0 34px rgba(255,0,127,0.25), inset 0 1px 0 rgba(255,255,255,0.34)",
          }}
        >
          <span style={{ whiteSpace: "nowrap" }}>{t("login.submit")}</span>
          <FaChevronRight
            aria-hidden="true"
            size={18}
            style={{ position: "absolute", right: 22, top: "50%", transform: "translateY(-50%)" }}
          />
        </button>
      </form>

      <AppleSignInButton onError={setError} />

      <button
        className="vq-auth-control"
        type="button"
        onClick={() => setShowRegister(true)}
        style={{
          marginTop: 14,
          width: "100%",
          maxWidth: 360,
          marginLeft: "auto",
          marginRight: "auto",
          display: "block",
          background: "transparent",
          textAlign: "left",
          border: "none",
          color: "rgba(255,255,255,0.76)",
          fontWeight: 850,
          letterSpacing: 0.35,
          textTransform: "none",
          cursor: "pointer",
          fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
          fontStyle: "normal",
          fontSize: 12,
          whiteSpace: "nowrap",
          padding: "0",
        }}
      >
        <span style={{ color: "rgba(255,255,255,0.62)" }}>{registerPrompt}</span>{" "}
        <span
          style={{
            color: "#f725a8",
            fontWeight: 1000,
            textShadow: "0 0 18px rgba(247,37,168,0.34)",
          }}
        >
          {registerAction}
        </span>
      </button>

      {/* Error */}
     {error && (
  <div
    style={{
      marginTop: 12,
      padding: "12px 14px",
      borderRadius: 14,
      background: "rgba(255,60,60,0.18)",
      border: "1px solid rgba(255,60,60,0.55)",
      color: "#ff4d4d",
      fontWeight: 900,
      letterSpacing: 0.5,
      textAlign: "center",
      textTransform: "uppercase",
    }}
  >
    {error}
  </div>
)}

      <div
        style={{
          position: "absolute",
          left: "calc(18px + var(--vq-safe-area-left))",
          right: "calc(18px + var(--vq-safe-area-right))",
          bottom: "calc(18px + var(--vq-safe-area-bottom))",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
        }}
      >
        <button
          type="button"
          onClick={() => navigate("/landing")}
          style={{
            border: "none",
            background: "transparent",
            color: "rgba(255,255,255,0.68)",
            padding: 0,
            fontWeight: 850,
            letterSpacing: 0.35,
            cursor: "pointer",
            fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
            fontSize: 12,
            lineHeight: 1.2,
            textShadow: "0 0 16px rgba(255,255,255,0.12)",
          }}
        >
          {vaultQuestInfoLabel}
        </button>
      </div>
    </div>
  </div>
) : (
  <div
  className={`vq-auth-page${isNativeAuthLayout ? " is-native" : ""}`}
  style={{
    height: "100dvh",
    display: "flex",
    justifyContent: "center",
    alignItems: "stretch",
    background: "#000",
    padding: 0,
    boxSizing: "border-box",
    overflow: "hidden",
  }}
>
   <div
  className="vq-auth-surface"
  style={{
    width: "100%",
    maxWidth: "430px",
    height: "100dvh",
    borderRadius: 0,
    overflow: "hidden",
    background: "linear-gradient(90deg, #080427 0%, #020047 100%)",
    position: "relative",
    boxShadow: "none",
    padding: "calc(26px + var(--vq-safe-area-top)) calc(18px + var(--vq-safe-area-right)) calc(26px + var(--vq-safe-area-bottom)) calc(18px + var(--vq-safe-area-left))",
    boxSizing: "border-box",
    fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
    color: "white",
  }}
>
      {/* Header */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 10 }}>
        <img
          src="/vaultquest-logo.png"
          alt="VaultQuest"
          style={{ width: 120, height: 120, objectFit: "contain", marginBottom: 10 }}
        />

       <div
  style={{
    fontSize: 18,
    fontWeight: 1000,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginBottom: 8,
    whiteSpace: "nowrap",
    textAlign: "center",
  }}
>
  {t("reset.title")}
</div>

        <div
          style={{
            opacity: 0.85,
            textAlign: "center",
            lineHeight: 1.35,
            fontWeight: 700,
            maxWidth: 320,
          }}
        >
          {t("reset.description")}
        </div>
      </div>

      {/* Content */}
      <div className="vq-auth-control" style={{ display: "grid", gap: 14, marginTop: 26, marginLeft: "auto", marginRight: "auto" }}>
        {/* Email Input */}
        <div
          style={{
            height: 62,
            borderRadius: 18,
            background: "white",
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "0 18px",
            boxSizing: "border-box",
          }}
        >
          <span style={{ fontSize: 22, opacity: 0.65 }}>✉️</span>
          <input
            type="email"
            placeholder={t("reset.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              border: "none",
              outline: "none",
              width: "100%",
              fontSize: 16,
              fontWeight: 900,
              letterSpacing: 0.6,
              fontStyle: "italic",
              color: "#222",
              background: "transparent",
              textTransform: "uppercase",
            }}
          />
        </div>

        {/* Send reset mail */}
        <button
          type="button"
          onClick={handleResetPassword}
          style={{
            marginTop: 6,
            height: 70,
            borderRadius: 20,
            border: "none",
            background: "linear-gradient(90deg, #ff007f 0%, #7a2bff 100%)",
            color: "white",
            fontWeight: 1000,
            fontSize: 18,
            letterSpacing: 1.0,
            textTransform: "uppercase",
            cursor: "pointer",
            boxShadow: "0 12px 30px rgba(0,0,0,0.35)",
          }}
        >
          {t("reset.sendButton")}
        </button>

        {/* Back to login */}
        <button
          type="button"
          onClick={() => {
            setError("");
            setView("LOGIN");
          }}
          style={{
            height: 60,
            borderRadius: 18,
            border: "2px solid rgba(255,255,255,0.65)",
            background: "rgba(255,255,255,0.08)",
            color: "white",
            fontWeight: 1000,
            letterSpacing: 0.9,
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          {t("reset.backToLogin")}
        </button>

        {/* Error / Info */}
        {error && (
          <div
            style={{
              marginTop: 4,
              padding: "10px 12px",
              borderRadius: 14,
              background: "rgba(255,255,255,0.10)",
              color: "rgba(255,255,255,0.95)",
              fontWeight: 900,
              letterSpacing: 0.4,
              textAlign: "center",
              lineHeight: 1.25,
            }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  </div>

      )}
    </>
  );
}

export default LoginPage;
